import React, { useCallback, useMemo } from 'react';
import clsx from 'clsx';
import { useIntl } from 'react-intl';
import { TypedMemo } from '../../common/types';
import { platformMeta } from '../promote/platformMeta';
import { isSocialPlatform, SocialPlatform, SocialPostType, socialPostTypes } from '../promote/types';
import { SocialPostsFilters as Filters } from './useSocialPostsFilters';
import { MUSIC24_MARK, socialMessages as messages, socialPostTypeMessages } from './socialMessages';
import { withBrandWord } from '../components/primitives/BrandWord';
/* eslint-disable css-modules/no-unused-class -- one stylesheet per screen; each part of the screen uses its own slice of it */
import styles from './Social.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface SocialPostsFiltersProps {
  filters: Filters;
  /** The connected channels, in the platform order. */
  platforms: SocialPlatform[];
  onPlatform: (platform: SocialPlatform | undefined) => void;
  onType: (type: SocialPostType | undefined) => void;
  onToggleOurs: () => void;
}

function isSocialPostType(value: string): value is SocialPostType {
  return (socialPostTypes as readonly string[]).includes(value);
}

/** The filters of the posts table: channel, type and the switch "Published with Music24". */
const SocialPostsFiltersComponent: React.FC<SocialPostsFiltersProps> = ({
  filters,
  platforms,
  onPlatform,
  onType,
  onToggleOurs,
}) => {
  const intl = useIntl();
  const oursLabel = intl.formatMessage(messages.filterOurs);

  const changePlatform = useCallback(
    (event: React.ChangeEvent<HTMLSelectElement>) => {
      const { value } = event.target;
      onPlatform(isSocialPlatform(value) ? value : undefined);
    },
    [onPlatform]
  );
  const changeType = useCallback(
    (event: React.ChangeEvent<HTMLSelectElement>) => {
      const { value } = event.target;
      onType(isSocialPostType(value) ? value : undefined);
    },
    [onType]
  );

  const platformOptions = useMemo(
    () =>
      platforms.map((platform) => (
        <option key={platform} value={platform}>
          {platformMeta[platform].name}
        </option>
      )),
    [platforms]
  );
  const typeOptions = useMemo(
    () =>
      socialPostTypes.map((type) => (
        <option key={type} value={type}>
          {intl.formatMessage(socialPostTypeMessages[type])}
        </option>
      )),
    [intl]
  );

  return (
    <div className={styles.filters}>
      <select
        className={styles.filterSelect}
        value={filters.platform ?? ''}
        aria-label={intl.formatMessage(messages.filterChannel)}
        onChange={changePlatform}
      >
        <option value="">{intl.formatMessage(messages.filterAllChannels)}</option>
        {platformOptions}
      </select>
      <select
        className={styles.filterSelect}
        value={filters.type ?? ''}
        aria-label={intl.formatMessage(messages.filterType)}
        onChange={changeType}
      >
        <option value="">{intl.formatMessage(messages.filterAllTypes)}</option>
        {typeOptions}
      </select>
      <button
        type="button"
        role="switch"
        aria-checked={filters.oursOnly}
        // The label is "Music24" on a phone. The accessible name stays the full text.
        aria-label={oursLabel}
        className={clsx(styles.toggle, filters.oursOnly && styles.toggleOn)}
        onClick={onToggleOurs}
      >
        <span className={styles.toggleTrack} />
        <span className={styles.desktopOnly}>{withBrandWord(oursLabel)}</span>
        <span className={styles.phoneOnly}>{MUSIC24_MARK}</span>
      </button>
    </div>
  );
};

export const SocialPostsFilters = TypedMemo(SocialPostsFiltersComponent);
